import React from "react";

import {
  DataTemplate,
  ValidationRule,
} from "@/core/datablocks/types/templates";
import { BaseUser } from "@/views/profiles/types/user";
import { SimpleModalProps } from "@/components_v2/Modal/types";
import { DataBlock } from "@/core/datablocks/types/blocks";
import { RowSelectionProps } from "@/components_v2/Tables/types";
import { ColumnType } from "antd/es/table/interface";
import { DataBlockRequest } from "./requests";
import { BasePaginationQueryParams } from "@/types/pagination";
import { RequestStatus } from "../invites/constants";
import { EnumFunctions } from "@/utilities/constants";
import { BaseErrorProps } from "@/components_v2/FormFields/types";
import { BaseData, BaseFilters } from "@/types/base";
import {
  FormFieldValue,
  FormFieldWithName,
} from "@/core/datablocks/types/types";
import { FileProgressStatus } from "@/views/files/components/CreateDataFileModal";
import { UploadFile } from "antd/es/upload/interface";
import { TablePaginationConfig } from "antd";

export interface DataBlocksFiltersProps {
  defaultFilters?: Partial<DataBlocksFiltersState>;
  filterNames: string[];
  onFiltersChange: (filters: DataBlocksFiltersState) => void;
}

export interface DataBlocksFiltersState extends BaseFilters {
  ids: number[];
  dataTemplates: number[];
  sharingRights: string[];
  contributors: number[];
  languages: number[];
  search?: string;
  user?: number;
  organizations?: number[];
  excludeOrganizations?: number[];
  prioritizeQuestion?: number;
}

export interface NormalizedDataBlock extends DataBlock {
  title?: string;
  contributors: number[];
  languages?: string[];
}

export interface DataBlockReference {
  label: string;
  id?: number;
  surveyId?: number;
  discussionId?: number;
  uuid?: string;
}

export interface DataBlockReferences {
  surveys: DataBlockReference[];
  surveyViews: DataBlockReference[];
  narratives: DataBlockReference[];
  discussions: DataBlockReference[];
  comments: DataBlockReference[];
}

export type DataBlocksQueryParams = BasePaginationQueryParams & {
  isExample?: boolean;
};

export type ColumnKey =
  | "title"
  | "type"
  | "organization"
  | "contributors"
  | "sharingRights"
  | "languages"
  | "updatedAt";
export type FilterKey =
  | "type"
  | "sharingRights"
  | "contributors"
  | "organizations"
  | "languages"
  | "search";

export type Tabs = {
  key: string;
  title: string;
  filters: FilterKey[];
  columns: ColumnKey[];
  defaultFilters: Record<string, any>;
};

export interface DataBlockTypeStepProps extends BaseErrorProps {
  templates: DataTemplate[];
  activeTemplateId?: number;
  handleTemplateChange: (value: number) => void;
}

export interface DataBlockDetailsStepProps {
  dataTemplate: DataTemplate;
  excludeFields?: (string | number)[];
}

export interface DataBlockDefaultValues {
  dataTemplate?: number;
  request?: DataBlockRequest;
}

export interface CreateDataBlockProps extends SimpleModalProps {
  onSubmit?: (data: DataBlock) => void;
  defaultValues?: DataBlockDefaultValues;
  filters?: Partial<DataBlocksFiltersState>;
}

export type DataBlockFormFields = {
  organization: number;
  dataTemplate: number;
  sharingRights: string;
  request?: number;
  fieldValues: FormFieldValue[];
};

export interface CreateDataBlockModalFooterProps {
  currentStep: number;
  isSubmitting: boolean;
  isNextEnabled: boolean;
  handleBack: () => void;
  handleNext: (value?: boolean) => void;
}

export interface DataBlockActionButtonsProps {
  dataBlock: DataBlock;
  dataTemplate: DataTemplate;
  setOpenDeleteModal: (value: boolean) => void;
  setEditRequestModal: (value: DataBlockRequestEditModalsState) => void;
}

export interface DataBlockSidebarProps {
  dataBlock: DataBlock;
  dataTemplate: DataTemplate;
  showCtas?: boolean;
}

export interface DataBlockContentProps {
  dataBlock: DataBlock;
  dataTemplate: DataTemplate;
}

export interface DataBlockDetailsProps {
  dataBlock: DataBlock;
  dataTemplate: DataTemplate;
}

export interface AddContributorModalProps extends SimpleModalProps {
  dataBlock: DataBlock;
  currentContributorIds: number[];
}

export interface AddContributorsFormValues {
  contributors: number[];
}

export interface DataBlockReferenceType {
  title: string;
  icon: React.FunctionComponent;
  getLink: (value: DataBlockReference) => string;
}

export type DataBlockReferencesTypes = {
  surveys: DataBlockReferenceType;
  surveyViews: DataBlockReferenceType;
  discussions: DataBlockReferenceType;
  comments: DataBlockReferenceType;
};

export interface DataBlocksTableProps<
  T extends BaseData,
  P extends BaseData = T,
> extends RowSelectionProps {
  columnNames: string[];
  defaultFilters?: Partial<DataBlocksFiltersState>;
  filterNames?: string[];
  additionalColumns?: Record<string, ColumnType<P>>;
  pagination?: false | TablePaginationConfig;
}

export interface ValidationRuleForSharingRights
  extends Omit<ValidationRule, "id" | "ruleFields"> {
  id?: number;
  ruleFields?: number[];
}

export interface FormFieldsWithSharingRights
  extends Omit<FormFieldWithName, "id" | "rules"> {
  id: string | number;
  rules?: ValidationRuleForSharingRights[];
}

export interface DataBlockReferencesProps {
  dataBlock: DataBlock;
}

export interface DataBlockRequestsTableProps<T> extends RowSelectionProps {
  columnNames: string[];
  defaultFilters?: Partial<DataBlocksFiltersState>;
  filterNames: string[];
  additionalColumns?: Record<string, ColumnType<T>>;
  className?: string;
}

export type DataBlockPermissionsOptions = {
  dataBlock?: DataBlock;
  dataTemplate?: DataTemplate;
};

type RequestStatusValue = (typeof RequestStatus)[keyof Omit<
  typeof RequestStatus,
  keyof typeof EnumFunctions
>]["value"];

export interface DataBlockRequestFiltersState
  extends BasePaginationQueryParams {
  search?: string;
  user?: number;
  dataTemplates?: number[];
  status?: RequestStatusValue[];
  excludeUser?: number;
}

export type DataBlockRequestFiltersProps = {
  setFilters: (value: DataBlockRequestFiltersState) => void;
  defaultFilters?: Partial<DataBlockRequestFiltersState>;
  filterNames: string[];
};

export enum ViewTypes {
  Tabs = "TABS",
  RequestsForm = "REQUESTS_FORM",
  EditRequestForm = "EDIT_REQUEST_FORM",
}

export enum RecipientType {
  User = "USER",
  UserInvite = "USER_INVITE",
}

export interface DataBlockRequestRecipient {
  email: string;
  type: RecipientType;
}

export interface UserRecipient extends BaseUser, DataBlockRequestRecipient {
  type: RecipientType.User;
}

export interface EmailRecipient extends DataBlockRequestRecipient {
  type: RecipientType.UserInvite;
}

export type Recipient = UserRecipient | EmailRecipient;

export interface EditRequestModalProps extends SimpleModalProps {
  request?: number;
}

export interface DataBlockRequestFormProps {
  form: any;
  currentStep: number;
  isSubmitting: boolean;
  showIndividualMessage: boolean;
  handleNextStep: () => void;
  handlePreviousStep: () => void;
  resetForm: () => void;
  handleShowIndividualMessageChange: (value: boolean) => void;
}

export interface DataBlockEditRequestFormProps {
  form: any;
  isSubmitting: boolean;
  dataBlockRequest?: DataBlockRequest;
  dataBlockTitle?: string;
  isFetching: boolean;
  handleSubmit: () => void;
}

export interface DataBlockRequestEditFormProps {
  formHook: DataBlockEditRequestFormProps;
}

export interface DataBlockRequestEditModalsState {
  open: boolean;
  request?: number;
}

export interface DataBlockRequestFormItem {
  email: string;
  role: string;
  message?: string;
}

export interface DataBlockRequestsTabProps {
  onCreateRequestsClick: () => void;
}

export interface DataBlockRequestsSurveyModalFooterProps {
  formHook: DataBlockRequestFormProps;
  onBackToTabs: () => void;
}

export interface DataBlockEditRequestsSurveyModalFooterProps {
  formHook: DataBlockEditRequestFormProps;
  onBackToTabs: () => void;
}

export enum activeTabs {
  dataBlocks = "dataBlocks",
  requests = "requests",
}

export enum UserInviteStatus {
  USER_INVITE = "USER_INVITE",
  USER = "USER",
}

export interface SurveyDataBlockModalProps extends SimpleModalProps {
  selectedBlocks: DataBlock[];
  setSelectedBlocks: (value: DataBlock[]) => void;
  filters: Partial<DataBlocksFiltersState>;
  initialData: Record<string, any>;
}

export interface DataBlocksTabProps {
  selectedBlocks: DataBlock[];
  setSelectedBlocks: (value: DataBlock[]) => void;
  currentQuestion?: number;
  filters: Partial<DataBlocksFiltersState>;
  onEditRequestClick: (value: number) => void;
  onCreateDataBlockClick: () => void;
}

export interface DataBlockEditRequestFormHookProps {
  request?: number;
  onSuccess: () => void;
}
export type FileProgressStatusType =
  (typeof FileProgressStatus)[keyof typeof FileProgressStatus];

export interface SelectedFile {
  name: string;
  status: FileProgressStatusType;
  file: UploadFile;
  error?: string;
}
